import { sortBy } from 'lodash'
import { z } from 'zod'

import { zodDate } from '../utilities/zodDate'
import {
  certificateProperties,
  onDemandCourseProperties,
  learningItemProperties,
  scheduledCourseEventProperties,
  scheduledCourseProperties,
  userLearningAssignmentProperties,
  LearningItemStatus,
  groupLearningAssignmentProperties,
  learningPathProperties,
} from './BaseModels'
import { bookingParser } from './Booking'
import cachedDummies from './cachedDummies'
import parseModel from './parseModel'
import { learningItemPermissions, learningItemPermissionsParser } from './Permissions'

/**********************************************************************
 * baseLearningItemProperties
 **********************************************************************/
const baseLearningItemProperties = {
  ...learningItemProperties,
  isFavorite: z.boolean(),
  userLearningAssignments: z.array(z.object(userLearningAssignmentProperties)).nullish(),
  groupLearningAssignments: z.array(z.object(groupLearningAssignmentProperties)).nullish(),
  certificate: z.object(certificateProperties).nullish(),
  learningProgress: z
    .object({
      userUuid: z.string(),
      onDemandCourseUuid: z.string().nullish(),
      scheduledCourseUuid: z.string().nullish(),
      learningPathUuid: z.string().nullish(),

      completionPercentage: z.coerce.number().nullish(),
      secondsTracked: z.number().nullish(), // For on demand courses
      secondsInvested: z.number().nullish(), // For scheduled courses
      firstAccessedAt: zodDate().nullish(),
      lastAccessedAt: zodDate().nullish(),
      completedAt: zodDate().nullish(),
      attempts: z.number().nullish(),
      updateNeeded: z.boolean().nullish(),
    })
    .nullish(),
  permissions: learningItemPermissionsParser,
  currentUserStatus: z.string().nullish(),
}

/**********************************************************************
 * OnDemandCourse
 **********************************************************************/
export const onDemandCourseParser = z.object({
  ...baseLearningItemProperties,
  kind: z.literal('onDemandCourse'),
  onDemandCourse: z.object({
    ...onDemandCourseProperties,
    booking: bookingParser.nullish(),
  }),
})

export type OnDemandCourse = z.infer<typeof onDemandCourseParser>

/**********************************************************************
 * LearningPath
 **********************************************************************/
export const learningPathParser = z.object({
  ...baseLearningItemProperties,
  kind: z.literal('learningPath'),
  learningPath: z.object({
    ...learningPathProperties,
  }),
})

export type LearningItemAsPath = z.infer<typeof learningPathParser>

export function isLearningItemAsPath(item: LearningItem): item is LearningItemAsPath {
  return item.kind === 'learningPath'
}

/**********************************************************************
 * ScheduledCourse
 **********************************************************************/
export const scheduledCourseParser = z.object({
  ...baseLearningItemProperties,
  kind: z.literal('scheduledCourse'),

  scheduledCourse: z.object({
    ...scheduledCourseProperties,
    events: z
      .array(
        z.object({
          ...scheduledCourseEventProperties,
          booking: bookingParser.nullish(),
        })
      )
      .transform((e) => sortBy(e, 'startDate')),
  }),
})

export type ScheduledCourse = z.infer<typeof scheduledCourseParser>

/**********************************************************************
 * LearningItem
 **********************************************************************/
export const learningItemParser = z.preprocess(
  (o: any) => {
    if (o) {
      o.kind = o?.onDemandCourseUuid ? 'onDemandCourse' : o?.scheduledCourseUuid ? 'scheduledCourse' : 'learningPath'
    }
    return o
  },
  z.discriminatedUnion('kind', [onDemandCourseParser, learningPathParser, scheduledCourseParser])
)

export type LearningItem = z.infer<typeof learningItemParser>

export const dummyLearningItem = cachedDummies(
  (uuid: string, error?: any): LearningItem => ({
    isDummy: true,
    error,

    uuid,

    categories: [
      {
        uuid: 'dummy-1',
        name: 'Lorem ipsum dolor sit amet',
      },
    ],

    status: LearningItemStatus.Published,

    type: 'online_class',

    title: 'Lorem ipsum dolor sit',
    description:
      'Aenean commodo ligula eget dolor. Aenean massa. Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus.',
    imageFileName: null,
    thumbnailFileName: null,

    isFavorite: false,

    kind: 'onDemandCourse',
    onDemandCourse: {
      uuid,
      durationInMin: 30,
      contentLocales: ['de-DE', 'en-US'],
      audioLocales: ['de-DE', 'en-US'],
      subtitleLocales: ['n/a'],
      booking: null,
      externalId: 'dummy',
      hasBookings: false,
    },

    userLearningAssignments: [],

    provider: null,

    createCertificate: false,
    certificateValidUntil: null,

    permissions: Object.fromEntries(Object.keys(learningItemPermissions).map((name) => [name, false])) as any,
  })
)

// TODO: Once we have different kinds of learning items, we need a dummy
// factory for each of them.
export const dummyCourse = (uuid: string, error?: any): OnDemandCourse =>
  dummyLearningItem(uuid, error) as OnDemandCourse

export const parseLearningItem = parseModel<LearningItem>(learningItemParser, dummyLearningItem)
